import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../PageLayout/LoginLayout";
import { verifyResetCode, requestPasswordReset } from "../services/auth.service";

export default function ResetVerifyCode() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    return qp.get("email") || location.state?.email || (() => {
      try { return localStorage.getItem("pendingPasswordResetEmail") || ""; } catch { return ""; }
    })();
  }, [location]);

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);

  const handleVerify = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setError("");
    if (!email) return setError("Email missing. Go back and enter your email.");
    if (!code) return setError("Enter the verification code from your email.");
    setLoading(true);
    try {
      await verifyResetCode(email, code);
      try { localStorage.setItem("pendingPasswordResetCode", String(code).trim()); } catch {}
      navigate("/change-password", { state: { email, code: String(code).trim() } });
    } catch (e) {
      const status = e?.response?.status;
      const backend = e?.response?.data;
      const msg = backend?.message || backend?.detail || e.message || "Invalid code.";
      // If backend rejects verify step with 400, continue to reset step where the code will be validated
      if (status === 400) {
        try { localStorage.setItem("pendingPasswordResetCode", String(code).trim()); } catch {}
        navigate("/change-password", { state: { email, code: String(code).trim() } });
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    try {
      await requestPasswordReset(email);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to resend code.";
      setError(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <Layout>
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md mx-auto">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" transform="scale(-1,1) translate(-20,0)" />
            </svg>
            Back
          </button>
          <h2 className="text-2xl font-semibold mb-2">Verify reset code</h2>
          <p className="text-sm text-gray-500 mb-6">We have sent a 6-digit code to {email}. Enter it below to continue.</p>
          <form onSubmit={handleVerify}>
            <input
              type="text"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
              className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
            />
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            <button type="submit" disabled={loading} className="w-full mt-4 bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium disabled:opacity-50">
              {loading ? "Verifying..." : "Continue"}
            </button>
            <button type="button" onClick={handleResend} disabled={resending} className="w-full mt-2 bg-gray-100 text-gray-800 rounded-md py-2 text-sm font-medium disabled:opacity-50">
              {resending ? "Sending..." : "Resend code"}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}


